import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedCluster } from 'store/carto/selectors';
import { Asset } from 'components/common/types/Asset.types';
import { FurnitureItem } from 'components/pages/Planner/PlannerMap/useFurniture.types';
import { formatAssetsInformation, formatFurnitureItem } from 'components/pages/Planner/PlannerMap/util';
import { clearClusterSelection } from 'store/carto/reducer';

interface UseCampaignPlanFurnitureReturn {
  furnitures: FurnitureItem[];
  onCloseFurnitureCard: () => void;
}

export const useCampaignPlanFurniture = ({
  localeCode,
  currencyCode,
}: {
  localeCode: string;
  currencyCode: string;
}): UseCampaignPlanFurnitureReturn => {
  const dispatch = useDispatch();
  const { furnitureCode, assets } = useSelector(getSelectedCluster);

  const furnitures = useMemo(() => {
    if (!assets?.length) return [];

    const formatAssets = (group: Asset[]): ReturnType<typeof formatAssetsInformation>[] =>
      group.map((asset) =>
        formatAssetsInformation({
          asset,
          currencyCode,
          localeCode,
          routeFrameCodes: [],
          shouldConvertPercentage: false,
        }),
      );

    const isEveryAssetSameFurniture = assets.every((asset) => asset.furnitureCode === furnitureCode);

    if (isEveryAssetSameFurniture) {
      const [{ longitude, latitude, address, postcode, district }] = assets;
      return [
        {
          ...formatFurnitureItem(furnitureCode ?? `${longitude}, ${latitude}`, {
            longitude,
            latitude,
            address,
            postcode,
            district,
          }),
          assetsInformation: formatAssets(assets),
        },
      ];
    }

    const grouped = assets.reduce<Record<string, Asset[]>>((acc, asset) => {
      const code = asset.furnitureCode ?? `${asset.longitude},${asset.latitude}`;
      if (!acc[code]) {
        acc[code] = [];
      }

      acc[code].push(asset);

      return acc;
    }, {});

    return Object.entries(grouped).map(([code, groupAssets]) => {
      const [{ longitude, latitude, address, postcode, district }] = groupAssets;

      return {
        ...formatFurnitureItem(code, { longitude, latitude, address, postcode, district }),
        assetsInformation: formatAssets(groupAssets),
      };
    });
  }, [furnitureCode, assets, localeCode, currencyCode]);

  const onCloseFurnitureCard = useCallback(() => {
    dispatch(clearClusterSelection());
  }, []);

  return { furnitures, onCloseFurnitureCard };
};
